/**
 * ZonaDialog — editor de zonas (barrio · localidad · departamento · cuadrante · punto+radio).
 *
 * Extraído de /pages/RadarMapa.jsx (Sprint UX + Publicado, 2026-02) para que
 * también pueda usarse desde /radar/tierra sin duplicar código.
 *
 * Contrato con la página padre (mismo que tenía en RadarMapa):
 *   onSaved(zonaCreadaOEditada)  → callback de éxito. Además admite dos
 *                                  props especiales (para preview en el mapa):
 *      onSaved._preview(polygonGeoJSON | null)
 *      onSaved._pin({lat, lng, draggable, onDrag} | null)
 *   Si el padre no las usa, ambos son opcionales.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Loader2, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PropreratiLocalityCombobox from "./PropreratiLocalityCombobox";

export default function ZonaDialog({ open, onClose, onSaved, zona, context = "propiedades" }) {
  const isTierra = context === "tierra";
  const isEdit = !!zona;
  const [tipo, setTipo] = useState("barrio");
  const [barriosList, setBarriosList] = useState([]);
  const [barrioId, setBarrioId] = useState("");
  const [barrioPolygon, setBarrioPolygon] = useState(null);
  const [nombre, setNombre] = useState("");
  const [localidad, setLocalidad] = useState("");
  const [intersecciones, setIntersecciones] = useState("");
  const [tiposFilter, setTiposFilter] = useState("terreno");
  // Fase Tierra 2026-07-26: cuando el modal se abre desde Tierra, sólo
  // permitimos los tipos que aplican al suelo (terreno + campo) como
  // checkbox múltiple. En Inmuebles queda el Select single-choice de
  // siempre. La fuente de verdad al guardar es `tiposFilter` en Inmuebles
  // y `tiposTierraSel` en Tierra.
  const [tiposTierraSel, setTiposTierraSel] = useState(["terreno"]);
  const [op, setOp] = useState("venta");
  const [autoCaptura, setAutoCaptura] = useState(false);
  const [umbralAbs, setUmbralAbs] = useState("");
  const [locList, setLocList] = useState([]);
  const [locId, setLocId] = useState("");
  // PASO 0 · autocomplete Properati para tipo=localidad.
  // Cuando el user elige del combobox, esto se llena con el PropreratiRef
  // (ver models.py). Persiste en la zona como `properati_ref`.
  const [properatiRef, setProperatiRef] = useState(null);
  const [locLookupQ, setLocLookupQ] = useState("");
  const [locLookupBusy, setLocLookupBusy] = useState(false);
  // Resolve en cascada: guarda el resultado de /localidades/resolve.
  // shape: {mode:"polygon"|"punto_radio", fuente_geometria, nombre, poligono?,
  //         punto?, radio_sugerido_km?, warning?, descartados?, tipo_admin?, departamento?}
  const [resolveResult, setResolveResult] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [referencia, setReferencia] = useState("");
  const [radioKm, setRadioKm] = useState(10);
  const [ptCenter, setPtCenter] = useState(null);
  const [ptBusy, setPtBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/barrios").then(r => setBarriosList(r.data.items || [])).catch(() => {});
    api.get("/localidades").then(r => setLocList(r.data.items || [])).catch(() => {});
  }, [open]);

  // Catálogo IDECOR filtrado + ordenado para el select.
  //   Departamentos: alfabético por nombre.
  //   Localidades:   alfabético A→Z GLOBAL por nombre (más fácil de buscar).
  //                  El depto sigue visible al lado del nombre en el label
  //                  ("Villa Allende — Colón · Municipio") para desambiguar.
  const locOptions = useMemo(() => {
    const filtered = locList.filter(l => l.tipo === tipo);
    return [...filtered].sort((a, b) =>
      (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" })
    );
  }, [locList, tipo]);

  // Preview del círculo en el mapa cuando el /resolve devolvió punto_radio
  // y estamos en tipo localidad/departamento (fallback honesto).
  useEffect(() => {
    if ((tipo !== "localidad" && tipo !== "departamento") || !resolveResult) return;
    if (resolveResult.mode !== "punto_radio" || !resolveResult.punto) return;
    const poly = circleToPolygon(resolveResult.punto.lat, resolveResult.punto.lng, Number(radioKm));
    setBarrioPolygon(poly);
    if (onSaved._preview) onSaved._preview(poly);
    if (onSaved._pin) onSaved._pin({
      lat: resolveResult.punto.lat, lng: resolveResult.punto.lng, draggable: false,
    });
    // eslint-disable-next-line
  }, [resolveResult, radioKm]);

  useEffect(() => {
    if (!open) { setPrefilled(false); return; }
    if (isEdit && !prefilled) {
      setTipo(zona.tipo || "barrio");
      setBarrioId(zona.barrio_id || "");
      setBarrioPolygon(zona.poligono || null);
      setNombre(zona.nombre || "");
      setLocalidad(zona.localidad || "");
      setIntersecciones((zona.intersecciones || []).join("\n"));
      setTiposFilter((zona.default_tipos && zona.default_tipos[0]) || "terreno");
      setOp(zona.default_operacion || "venta");
      setAutoCaptura(!!zona.auto_captura);
      setUmbralAbs(zona.umbral_absoluto_usd_m2 ? String(zona.umbral_absoluto_usd_m2) : "");
      setLocId(zona.loc_id || "");
      setProperatiRef(zona.properati_ref || null);
      setReferencia(zona.referencia || "");
      setRadioKm(zona.radio_km || 10);
      if (zona.center) setPtCenter({ lat: zona.center.lat, lng: zona.center.lng });
      setPrefilled(true);
    }
  }, [open, isEdit, zona, prefilled]);

  useEffect(() => {
    if (tipo !== "barrio" || !barrioId) return;
    api.get(`/barrios/${barrioId}`).then(r => {
      setBarrioPolygon(r.data.poligono);
      if (!isEdit && !nombre) setNombre(r.data.nombre);
      if (!isEdit && !localidad) setLocalidad(r.data.nombre);
    }).catch(() => setBarrioPolygon(null));
    // eslint-disable-next-line
  }, [barrioId, tipo]);

  useEffect(() => {
    if ((tipo !== "localidad" && tipo !== "departamento") || !locId) {
      setResolveResult(null);
      return;
    }
    // Resolve en cascada: IDECOR ejido → OSM ejido → punto+radio.
    // Actualiza barrioPolygon (para preview) y resolveResult (para UI).
    const doc = locList.find(l => l.loc_id === locId);
    setResolving(true);
    api.post(`/localidades/resolve`, { loc_id: locId }).then(r => {
      const res = r.data;
      setResolveResult(res);
      if (res.mode === "polygon" && res.poligono) {
        setBarrioPolygon(res.poligono);
        if (!isEdit && !nombre && res.nombre) setNombre(res.nombre);
      } else if (res.mode === "punto_radio" && res.punto) {
        // Fallback: prellenar centro + radio sugerido, cambiar visual a modo aprox.
        setBarrioPolygon(null);
        setPtCenter({ lat: res.punto.lat, lng: res.punto.lng });
        setRadioKm(res.radio_sugerido_km || 5);
        if (!isEdit && !nombre && res.nombre) {
          setNombre(`${res.nombre} · radio ${res.radio_sugerido_km || 5} km`);
        }
      }
    }).catch((e) => {
      setResolveResult(null);
      setBarrioPolygon(null);
      const detail = e.response?.data?.detail;
      if (detail) toast.error(detail);
    }).finally(() => setResolving(false));
    // eslint-disable-next-line
  }, [locId, tipo]);

  const lookupLocalidad = async () => {
    if (!locLookupQ.trim()) return;
    setLocLookupBusy(true);
    try {
      const res = await api.post("/localidades/lookup", {
        q: locLookupQ.trim(),
        tipo: tipo === "departamento" ? "departamento" : "localidad",
      });
      const found = res.data;
      const list = await api.get("/localidades");
      setLocList(list.data.items || []);
      setLocId(found.loc_id);
      setLocLookupQ("");
      toast.success(`Cargada: ${found.nombre}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el lugar");
    } finally { setLocLookupBusy(false); }
  };

  const ubicarPunto = async () => {
    if (!referencia.trim()) return;
    setPtBusy(true);
    try {
      const r = await api.post("/localidades/geocode-point", { q: referencia.trim() });
      setPtCenter({ lat: r.data.lat, lng: r.data.lng });
      toast.success("Punto ubicado — arrastrá el pin para ajustar");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos geolocalizar ese punto");
    } finally { setPtBusy(false); }
  };

  const circleToPolygon = (lat, lng, km, segments = 48) => {
    if (!lat || !lng || !km) return null;
    const EARTH_KM = 6371.0088;
    const latR = (lat * Math.PI) / 180;
    const lngR = (lng * Math.PI) / 180;
    const ang = km / EARTH_KM;
    const coords = [];
    for (let i = 0; i < segments; i++) {
      const bearing = (2 * Math.PI * i) / segments;
      const newLat = Math.asin(Math.sin(latR) * Math.cos(ang) +
                                Math.cos(latR) * Math.sin(ang) * Math.cos(bearing));
      const newLng = lngR + Math.atan2(
        Math.sin(bearing) * Math.sin(ang) * Math.cos(latR),
        Math.cos(ang) - Math.sin(latR) * Math.sin(newLat),
      );
      coords.push([(newLng * 180) / Math.PI, (newLat * 180) / Math.PI]);
    }
    coords.push(coords[0]);
    return { type: "Polygon", coordinates: [coords] };
  };

  useEffect(() => {
    if (tipo !== "punto_radio") {
      if (onSaved._pin) onSaved._pin(null);
      return;
    }
    if (ptCenter && radioKm) {
      const poly = circleToPolygon(ptCenter.lat, ptCenter.lng, Number(radioKm));
      setBarrioPolygon(poly);
      if (onSaved._preview) onSaved._preview(poly);
      if (onSaved._pin) onSaved._pin({ ...ptCenter, draggable: true,
                                        onDrag: (lat, lng) => setPtCenter({ lat, lng }) });
    }
    // eslint-disable-next-line
  }, [ptCenter, radioKm, tipo]);

  useEffect(() => {
    if (onSaved._preview) onSaved._preview(barrioPolygon);
  }, [barrioPolygon, onSaved]);

  const reset = () => {
    setTipo("barrio"); setBarrioId(""); setBarrioPolygon(null);
    setNombre(""); setLocalidad(""); setIntersecciones("");
    setTiposFilter("terreno"); setOp("venta"); setAutoCaptura(false); setUmbralAbs(""); setPrefilled(false);
    setLocId(""); setLocLookupQ(""); setReferencia(""); setRadioKm(10); setPtCenter(null);
  };

  const handleClose = () => {
    if (onSaved._preview) onSaved._preview(null);
    if (onSaved._pin) onSaved._pin(null);
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (tipo === "barrio" && !barrioId) return toast.error("Elegí un barrio del listado");
    if (tipo === "departamento" && !locId)
      return toast.error("Elegí (o cargá) un departamento del listado");
    if (tipo === "localidad") {
      // En Tierra alcanza con la localidad IDECOR (baldíos catastrales).
      // En Propiedades se pide el ref Properati verificado (o fallback IDECOR).
      if (context === "propiedades" && !properatiRef?.nombre_canonico)
        return toast.error("Elegí una localidad del autocomplete Properati (o del fallback IDECOR)");
      if (context !== "propiedades" && !locId)
        return toast.error("Elegí una localidad del catálogo provincial");
    }
    if (tipo === "punto_radio" && !referencia)
      return toast.error("Escribí un punto de referencia (ej. Dique Los Molinos)");
    if (tipo === "punto_radio" && (!radioKm || Number(radioKm) <= 0))
      return toast.error("Radio en km > 0");
    if (!nombre) return toast.error("Nombre requerido");
    // Regla clave (Sprint 1.6): si el usuario eligió una localidad y el
    // /resolve devolvió punto_radio (no hay ejido en OSM), guardamos la zona
    // como tipo=punto_radio HONESTA — el nombre lleva "· radio X km" y el
    // storage refleja que es un área aproximada.
    const fallbackPuntoRadio = (
      (tipo === "localidad" || tipo === "departamento") &&
      resolveResult?.mode === "punto_radio"
    );
    setSaving(true);
    try {
      const tipoFinal = fallbackPuntoRadio ? "punto_radio" : tipo;
      // Tierra: default_tipos viene del multi-select restringido
      // (terreno/campo). Inmuebles: [tiposFilter] como siempre.
      // Tierra fija operación a venta (la tierra no se alquila).
      const finalTipos = isTierra
        ? (tiposTierraSel.length ? tiposTierraSel : ["terreno"])
        : [tiposFilter];
      const finalOp = isTierra ? "venta" : op;
      const body = {
        nombre, tipo: tipoFinal,
        localidad: localidad || nombre,
        default_tipos: finalTipos,
        default_operacion: finalOp,
        auto_captura: autoCaptura,
        umbral_absoluto_usd_m2: umbralAbs === "" ? null : Number(umbralAbs),
      };
      if (tipoFinal === "barrio") body.barrio_id = barrioId;
      if (tipoFinal === "localidad" || tipoFinal === "departamento") body.loc_id = locId;
      // Persistimos el PropreratiRef sólo si el user eligió una opción
      // verificada del combobox (nunca del fallback IDECOR — ese no tiene slug).
      if (properatiRef?.slug_verificado) {
        body.properati_ref = {
          properati_id: properatiRef.properati_id,
          nombre_canonico: properatiRef.nombre_canonico,
          slug_verificado: properatiRef.slug_verificado,
          provincia: properatiRef.provincia,
          verificado_at: properatiRef.verificado_at || new Date().toISOString(),
          resultados_totales: properatiRef.resultados_totales || 0,
        };
      }
      if (tipoFinal === "punto_radio") {
        body.referencia = fallbackPuntoRadio
          ? `${resolveResult.nombre} (punto oficial IDECOR)`
          : referencia;
        body.radio_km = Number(radioKm);
        if (fallbackPuntoRadio && resolveResult.punto) {
          body.center_lat = resolveResult.punto.lat;
          body.center_lng = resolveResult.punto.lng;
        } else if (ptCenter) {
          body.center_lat = ptCenter.lat;
          body.center_lng = ptCenter.lng;
        }
        if (fallbackPuntoRadio) {
          body.loc_id = locId;  // vínculo al doc del catálogo IDECOR
          body.fuente_geometria = "punto_radio";
        }
      }
      if (tipoFinal === "cuadrante") {
        const list = intersecciones.split("\n").map(s => s.trim()).filter(Boolean);
        if (list.length < 3) { setSaving(false); return toast.error("Cargá al menos 3 intersecciones"); }
        body.intersecciones = list;
      }
      let res;
      if (isEdit) {
        res = await api.patch(`/zonas/${zona.zona_id}`, body);
        toast.success(`Zona "${res.data.nombre}" actualizada`);
      } else {
        body.region_id = "reg_cordoba_ar";
        res = await api.post("/zonas", body);
        toast.success(`Zona "${res.data.nombre}" creada`);
      }
      onSaved(res.data);
      handleClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? "Error al actualizar zona" : "Error al crear zona"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="rounded-none max-w-lg z-[1200] max-h-[90vh] overflow-y-auto" data-testid="zona-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Editar zona" : "Nueva zona"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {isEdit
              ? "Cambiá los parámetros de búsqueda. Los datos ya capturados quedan intactos."
              : "Elegí un barrio real o dibujá un cuadrante. Vas a ver el polígono en el mapa."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Tipo</Label>
              <Select value={tipo} onValueChange={(v) => { setTipo(v); setBarrioId(""); setBarrioPolygon(null); }}>
                <SelectTrigger data-testid="zona-tipo" className="rounded-none border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="barrio">Barrio (de Córdoba)</SelectItem>
                  <SelectItem value="localidad">Localidad</SelectItem>
                  <SelectItem value="departamento">Departamento</SelectItem>
                  <SelectItem value="cuadrante">Cuadrante (4 calles)</SelectItem>
                  <SelectItem value="punto_radio">Punto + Radio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Buscar</Label>
              {isTierra ? (
                // Tierra: solo tipos de suelo (terreno + campo). Checkbox
                // múltiple. Filtramos las otras opciones porque son
                // Inmuebles, no aplican al mapa de tierra.
                <div className="mt-1 border border-slate-300 px-2 py-2 flex gap-4" data-testid="zona-tipos-tierra">
                  {["terreno", "campo"].map(t => {
                    const checked = tiposTierraSel.includes(t);
                    return (
                      <label key={t} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          data-testid={`zona-tipo-tierra-${t}`}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTiposTierraSel(prev => prev.includes(t) ? prev : [...prev, t]);
                            } else {
                              setTiposTierraSel(prev => {
                                const next = prev.filter(x => x !== t);
                                // Regla honestidad: no permitir 0 elegidos
                                // (deja default_tipos vacío → captura sin sentido).
                                return next.length ? next : prev;
                              });
                            }
                          }}
                          className="accent-emerald-700"
                        />
                        <span className="capitalize">{t}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <Select value={tiposFilter} onValueChange={setTiposFilter}>
                  <SelectTrigger data-testid="zona-tipos" className="rounded-none border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["terreno","casa","departamento","local","oficina","campo"].map(x => (
                      <SelectItem key={x} value={x}>{x[0].toUpperCase()+x.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {tipo === "barrio" && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Barrio ({barriosList.length} disponibles)</Label>
              <Select value={barrioId} onValueChange={setBarrioId}>
                <SelectTrigger data-testid="zona-barrio-select" className="rounded-none border-slate-300 mt-1">
                  <SelectValue placeholder="Elegí un barrio..." />
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  {barriosList.map(b => (
                    <SelectItem key={b.barrio_id} value={b.barrio_id}>{b.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {barrioPolygon && (
                <p className="text-[11px] text-emerald-700 mt-1.5">✓ Polígono cargado — se muestra sobre el mapa</p>
              )}
            </div>
          )}

          <div className={isTierra ? "" : "grid grid-cols-2 gap-4"}>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Nombre de la zona</Label>
              <Input data-testid="zona-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)}
                     placeholder="Ej: Alberdi - captación"
                     className="rounded-none border-slate-300 mt-1" />
            </div>
            {!isTierra && (
              // Tierra fija operación a "venta" (la tierra no se alquila).
              // En Inmuebles se sigue mostrando el selector completo.
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Operación</Label>
                <Select value={op} onValueChange={setOp}>
                  <SelectTrigger data-testid="zona-op" className="rounded-none border-slate-300 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="venta">Venta</SelectItem>
                    <SelectItem value="alquiler">Alquiler</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {(tipo === "localidad" || tipo === "departamento") && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">
                {tipo === "departamento" ? "Departamento" : "Localidad"} — catálogo oficial provincial
              </Label>

              {tipo === "localidad" && context === "propiedades" ? (
                // ── Propiedades · autocomplete Properati + fallback IDECOR ─
                // El combobox habla del catálogo de AVISOS (Properati) — sólo
                // tiene sentido cuando el motor que se está configurando es el
                // de avisos publicados. En Tierra (baldíos IDECOR) mostramos
                // el Select estático original (ver rama else).
                <div className="mt-1">
                  <PropreratiLocalityCombobox
                    value={properatiRef}
                    onSelect={(item) => {
                      const ref = item.verificable ? {
                        properati_id: item.properati_id,
                        nombre_canonico: item.nombre_canonico,
                        slug_verificado: item.slug_verificado,
                        provincia: item.provincia,
                        verificado_at: new Date().toISOString(),
                        resultados_totales: item.resultados_totales || 0,
                      } : null;
                      setProperatiRef(item.verificable ? ref : { ...item });
                      const match = locList.find(l =>
                        l.tipo === "localidad" &&
                        (l.nombre || "").toLowerCase() === (item.nombre_canonico || "").toLowerCase()
                      );
                      setLocId(match?.loc_id || "");
                      if (!isEdit && !nombre) setNombre(item.nombre_canonico);
                    }}
                  />
                </div>
              ) : (
                // ── Tierra + Departamento · Select estático IDECOR ────────
                // Comportamiento pre-PASO-0 (regresión-safe): 551 localidades
                // ordenadas A→Z desde el catálogo oficial de la Provincia.
                <Select value={locId} onValueChange={setLocId}>
                  <SelectTrigger data-testid="zona-loc-select" className="rounded-none border-slate-300 mt-1">
                    <SelectValue placeholder={`Elegí ${tipo === "departamento" ? "un departamento" : "una localidad"}...`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {locOptions.map(l => (
                      <SelectItem key={l.loc_id} value={l.loc_id}>
                        {tipo === "localidad"
                          ? `${l.nombre} — ${l.departamento || "s/d"}${l.tipo_admin === "Comuna" ? " · Comuna" : ""}`
                          : l.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <p className="text-[11px] text-slate-500 mt-1">
                {tipo === "localidad" && context === "propiedades"
                  ? "Autocomplete Properati con verificación de provincia (Córdoba)."
                  : tipo === "localidad"
                    ? `${locOptions.length} localidades oficiales del catastro provincial.`
                    : `${locOptions.length} departamentos oficiales del catastro provincial.`}
              </p>

              {/* Badge de resultado de /resolve: muestra CUÁL es la fuente
                  geométrica del ejido (IDECOR nativo, OSM validado, o
                  fallback punto+radio con warning honesto). */}
              {resolving && (
                <p className="text-[11px] text-slate-500 mt-2 inline-flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Resolviendo geometría…
                </p>
              )}
              {!resolving && resolveResult && resolveResult.mode === "polygon" && (
                <div className="mt-2 flex items-start gap-2 border border-emerald-600 bg-emerald-50 p-2 text-xs"
                     data-testid="resolve-badge-polygon">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
                  <div className="text-emerald-900">
                    <span className="font-bold">
                      {resolveResult.fuente_geometria === "idecor_ejido"
                        ? "Ejido oficial · catastro provincial"
                        : "Ejido según OpenStreetMap"}
                    </span>
                    <p className="text-[11px] mt-0.5">Polígono cargado sobre el mapa.</p>
                  </div>
                </div>
              )}
              {!resolving && resolveResult && resolveResult.mode === "punto_radio" && (
                <div className="mt-2 border border-amber-500 bg-amber-50 p-2 text-xs space-y-2"
                     data-testid="resolve-badge-punto-radio">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-amber-900">
                      <span className="font-bold">Área aproximada — no es el ejido oficial</span>
                      <p className="text-[11px] mt-0.5">
                        OpenStreetMap no publica el contorno de esta localidad. Se usa un círculo
                        centrado en el punto oficial del catastro. Ajustá el radio antes de crear la zona.
                      </p>
                    </div>
                  </div>
                  <div className="pt-1 border-t border-amber-300">
                    <div className="flex items-baseline justify-between mb-1">
                      <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-amber-900">Radio</Label>
                      <span className="font-mono text-xs font-bold text-amber-900" data-testid="resolve-radio-value">
                        {radioKm} km
                      </span>
                    </div>
                    <input
                      type="range" min={1} max={30} step={1}
                      data-testid="resolve-radio-slider"
                      value={radioKm}
                      onChange={(e) => setRadioKm(Number(e.target.value))}
                      className="w-full accent-amber-700"
                    />
                    <div className="flex justify-between text-[10px] text-amber-800 mt-0.5">
                      <span>1 km</span><span>10</span><span>20</span><span>30 km</span>
                    </div>
                  </div>
                  {resolveResult.descartados?.length > 0 && (
                    <div className="pt-1 border-t border-amber-300">
                      <p className="text-[10px] text-amber-800 flex items-start gap-1">
                        <Info className="w-3 h-3 shrink-0 mt-0.5" />
                        <span>
                          Se descartaron {resolveResult.descartados.length} polígono(s) que apuntaban a
                          {" "}otro departamento (homónimos evitados).
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tipo === "punto_radio" && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Punto de referencia</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    data-testid="zona-referencia"
                    value={referencia}
                    onChange={(e) => setReferencia(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), ubicarPunto())}
                    placeholder="Ej: Dique Los Molinos, Córdoba"
                    className="rounded-none border-slate-300"
                  />
                  <Button type="button" variant="outline" onClick={ubicarPunto}
                          disabled={ptBusy || !referencia.trim()}
                          data-testid="zona-ubicar-btn"
                          className="rounded-none border-slate-300 shrink-0">
                    {ptBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Ubicar"}
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Presioná «Ubicar» y arrastrá el pin en el mapa para ajustar el centro.
                </p>
              </div>
              <div>
                <div className="flex items-baseline justify-between mb-1">
                  <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Radio</Label>
                  <span className="font-mono text-sm font-bold text-emerald-800" data-testid="radio-value">
                    {radioKm} km
                  </span>
                </div>
                <input
                  type="range" min={1} max={100} step={1}
                  data-testid="zona-radio-km"
                  value={radioKm}
                  onChange={(e) => setRadioKm(Number(e.target.value))}
                  className="w-full accent-emerald-700"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                  <span>1 km</span><span>25</span><span>50</span><span>75</span><span>100 km</span>
                </div>
              </div>
              {ptCenter && (
                <p className="text-[11px] text-emerald-700">
                  ✓ Centro: {ptCenter.lat.toFixed(4)}, {ptCenter.lng.toFixed(4)} · círculo pintado sobre el mapa
                </p>
              )}
            </div>
          )}

          {tipo === "cuadrante" && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">Intersecciones (una por línea, ≥3)</Label>
              <Textarea rows={4} value={intersecciones} onChange={(e) => setIntersecciones(e.target.value)}
                placeholder="Duarte Quirós y Colón&#10;Duarte Quirós y Cañada&#10;Bv. San Juan y Colón&#10;Bv. San Juan y Cañada"
                className="rounded-none border-slate-300 mt-1 font-mono text-xs" />
              <p className="text-[11px] text-slate-500 mt-1">La geolocalización tarda ~1s por intersección.</p>
            </div>
          )}

          <details className="border border-slate-200 bg-slate-50" data-testid="zona-umbral-details">
            <summary className="cursor-pointer px-3 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-slate-600 hover:text-emerald-800 select-none">
              Avanzado · Umbral absoluto por zona
            </summary>
            <div className="px-3 pb-3 pt-1">
              <Label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-700">
                Bajo US$/m² es oportunidad
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-slate-500">US$</span>
                <Input
                  data-testid="zona-umbral-abs"
                  type="number"
                  min={0}
                  step={10}
                  value={umbralAbs}
                  onChange={(e) => setUmbralAbs(e.target.value)}
                  placeholder="dejar vacío = usar % global"
                  className="rounded-none border-slate-300 h-9 max-w-[160px]"
                />
                <span className="text-xs text-slate-500">/ m²</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                Si está seteado, esta zona usa este umbral en vez del % global.
                Sólo aplica a avisos en USD. Dejalo vacío para volver al criterio %.
              </p>
            </div>
          </details>

          <div className="border border-slate-200 p-3 bg-slate-50">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="zona-auto-captura"
                checked={autoCaptura}
                onChange={(e) => setAutoCaptura(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-emerald-700"
              />
              <div className="flex-1">
                <div className="text-xs font-bold text-slate-900">Búsqueda automática diaria</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Cuando esté activo, actualizamos los datos de esta zona una vez por día.
                  Podés seguir disparando búsquedas manuales en cualquier momento.
                </div>
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} className="rounded-none border-slate-300">Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="zona-submit" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isEdit ? "Guardar cambios" : "Crear zona"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
